import styled from 'styled-components';
import {
  IS_TAB_OPEN_DATE,
  SATELLITE_END_DATE,
  SHOW_QR_DATE,
  useDateHook,
} from '../hook/useDateHook';
import { getEnv, isPc } from '../utils';
import { useState, useMemo, Activity } from 'react';
import dayjs from 'dayjs';

export const TestBox = () => {
  const env = getEnv();
  const [visible, setVisible] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(4);
  const [selectedDay, setSelectedDay] = useState(1);
  const [selectedTime, setSelectedTime] = useState('00:00');
  const [selectedMinute, setSelectedMinute] = useState('00');
  const { setTodayDate, today, setToday } = useDateHook();

  // 월 선택 옵션 (4월, 5월)
  const monthOptions = useMemo(
    () => [
      { value: dayjs().month() - 1, label: `${dayjs().month() - 1}월` },
      { value: dayjs().month(), label: `${dayjs().month()}월` },
      { value: dayjs().month() + 1, label: `${dayjs().month() + 1}월` },
      { value: dayjs().month() + 2, label: `${dayjs().month() + 2}월` },
    ],
    [],
  );

  // 일 선택 옵션 (1일 ~ 31일)
  const dayOptions = useMemo(() => {
    return Array.from({ length: 30 }, (_, i) => ({
      value: i + 1,
      label: `${i + 1}일`,
    }));
  }, []);

  // 시간 선택 옵션 (00:00 ~ 23:00)
  const timeOption = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => {
        return { value: `${i.toString().padStart(2, '0')}:00`, label: `${i}시` };
      }),
    [],
  );

  // 분 선택 옵션 (00 ~ 59)
  const minuteOption = useMemo(
    () =>
      Array.from({ length: 60 }, (_, i) => {
        const minute = i.toString().padStart(2, '0');
        return { value: minute, label: `${minute}분` };
      }),
    [],
  );

  // 사용자 정의 날짜 설정
  const applyCustomDate = () => {
    const date = dayjs(
      `2025-${selectedMonth.toString().padStart(2, '0')}-${selectedDay
        .toString()
        .padStart(2, '0')}T00:00:00`,
    );
    if (date.isValid()) {
      setToday(date.format('YYYY-MM-DDTHH:mm:ss'));
    }
  };

  if (isPc()) return null; // PC 환경에서는 표시하지 않음
  if (env === 'live') return null;

  return (
    <>
      {!visible && <ToggleButton onClick={() => setVisible(true)}>🔧</ToggleButton>}
      <Activity mode={visible ? 'visible' : 'hidden'}>
        <Wrap>
          <Header>
            <HeaderTitle>🔧 개발 테스트 도구</HeaderTitle>
            <Close onClick={() => setVisible(false)}>×</Close>
          </Header>

          <Title>현재 날짜: {today.format('YYYY-MM-DD HH:mm:ss')}</Title>

          <ButtonGroup>
            <ButtonLabel>날짜 프리셋</ButtonLabel>
            <Button onClick={() => setTodayDate()}>📅 오늘로 설정</Button>
            <Button onClick={() => setToday(dayjs(IS_TAB_OPEN_DATE).add(1, 'second'))}>
              🎮 {dayjs(IS_TAB_OPEN_DATE).add(1, 'second').format('MM월 DD일')} - 새틀라이트 시작
            </Button>
            <Button onClick={() => setToday(dayjs(SATELLITE_END_DATE).add(1, 'second'))}>
              🚀 {dayjs(SATELLITE_END_DATE).add(1, 'second').format('MM월 DD일 hh시 mm분')}
              <br /> 새틀라이트 종료(새벽2시)
            </Button>
            <Button onClick={() => setToday(dayjs(SHOW_QR_DATE).add(1, 'second'))}>
              🏆 {dayjs(SHOW_QR_DATE).add(1, 'second').format('MM월 DD일 hh시 mm분')} - QR 노출
            </Button>
          </ButtonGroup>

          <Divider />

          <ButtonGroup>
            <ButtonLabel>페이지 URL</ButtonLabel>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert('URL이 클립보드에 복사되었습니다!');
              }}
            >
              📋 현재 페이지 URL 복사
            </Button>
          </ButtonGroup>

          <Divider />

          <ButtonGroup>
            <ButtonLabel>날짜 직접 선택 (2025년)</ButtonLabel>
            <DateSelectContainer>
              <SelectBox
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
              >
                {monthOptions.map((option) => (
                  <option key={`month-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectBox>
              <SelectBox
                value={selectedDay}
                onChange={(e) => setSelectedDay(Number(e.target.value))}
              >
                {dayOptions.map((option) => (
                  <option key={`day-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectBox>
              <SelectBox value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)}>
                {timeOption.map((option) => (
                  <option key={`time-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectBox>
              <SelectBox value={selectedMinute} onChange={(e) => setSelectedMinute(e.target.value)}>
                {minuteOption.map((option) => (
                  <option key={`minute-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectBox>
            </DateSelectContainer>
            <ApplyButton onClick={applyCustomDate}>적용</ApplyButton>
          </ButtonGroup>
        </Wrap>
      </Activity>
    </>
  );
};

const Wrap = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  width: 300px;
  border: 2px solid #4caf50;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  background-color: white;
  padding: 15px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const Title = styled.p`
  font-size: 14px;
  color: #333;
  margin-bottom: 10px;
  font-weight: bold;
  border-left: 3px solid #4caf50;
  padding-left: 8px;

  &:nth-child(3) {
    background-color: #e8f5e9;
    padding: 8px;
    border-radius: 5px;
    border: 1px solid #4caf50;
    margin-bottom: 14px;
    border-left: 3px solid #4caf50;
  }
`;

const Button = styled.button`
  margin-bottom: 7px;
  font-size: 12px;
  padding: 8px;
  background-color: #f8f8f8;
  border: 1px solid #ddd;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;

  &:hover {
    background-color: #e8e8e8;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(1px);
  }
`;

const Close = styled.button`
  position: absolute;
  top: 5px;
  right: 10px;
  width: 24px;
  height: 24px;
  border: none;
  background-color: #ff5252;
  color: white;
  border-radius: 50%;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #ff0000;
    transform: scale(1.1);
  }
`;

const Divider = styled.hr`
  margin: 12px 0;
  border: 0;
  height: 1px;
  background-image: linear-gradient(
    to right,
    rgba(0, 0, 0, 0),
    rgba(76, 175, 80, 0.5),
    rgba(0, 0, 0, 0)
  );
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  border-bottom: 2px solid #4caf50;
  padding-bottom: 10px;
`;

const HeaderTitle = styled.h3`
  font-size: 16px;
  color: #333;
  margin: 0;
  font-weight: bold;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
`;

const ButtonLabel = styled.div`
  font-size: 13px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #555;
  border-left: 3px solid #4caf50;
  padding-left: 8px;
`;

const SelectBox = styled.select`
  flex: 1;
  padding: 8px;
  font-size: 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: white;
  margin-right: 5px;
`;

const DateSelectContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const ApplyButton = styled.button`
  width: 100%;
  padding: 8px;
  font-size: 13px;
  background-color: #4caf50;
  color: white;
  border: 1px solid #43a047;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: bold;

  &:hover {
    background-color: #3d8b40;
  }
`;

const ToggleButton = styled.button`
  position: fixed;
  top: 10px;
  left: 10px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #4caf50;
  color: white;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  z-index: 999;
  transition: all 0.2s;

  &:hover {
    transform: scale(1.1);
    background-color: #3d8b40;
  }
`;
